import { ELabelTextSize, LabelText } from 'elements/text/LabelText';
import { useImageCreator } from 'hooks/marker/useImageCreator';
import { useImageUploader } from 'hooks/marker/useImageUploader';
import { IUploadedImage } from 'libs/hooks/io/useUploadImage';
import { EUploadResolution } from 'libs/utils/logic/operations/fileOperations';
import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import styled from 'styled-components/macro';

interface Props {
    controlsRef: React.RefObject<HTMLDivElement[]>;
}

export const ImageUploader = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const [batchInProgress, setBatchInProgress] = useState<boolean>(false);

    const imageUploader = useImageUploader(
        [EUploadResolution.SMALL, EUploadResolution.BIG, EUploadResolution.ULTRA],
        onFinished
    );

    const imageCreator = useImageCreator();

    useEffect(() => {
        if (ctxEdit.filesToUpload?.length > 0 && !batchInProgress) {
            setBatchInProgress(true);
            const first = ctxEdit.filesToUpload[0];

            ctxEdit.setFilesToUpload(ctxEdit.filesToUpload.slice(1));
            imageUploader.uploadFiles(first.fileList, first.lat, first.lng, first.zoom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctxEdit.filesToUpload, batchInProgress]);

    const getOffsetForMultiUpload = (lat: number, lng: number) => {
        const HORIZONTAL_OFFSET = 300;

        const point = ctxEdit.mapRef?.current?.getMap().project([lng, lat]);
        const newLatLng = ctxEdit.mapRef?.current
            ?.getMap()
            .unproject([point.x + HORIZONTAL_OFFSET, point.y]);
        return newLatLng.lng - lng;
    };

    function onFinished(uploaded: IUploadedImage[], lat: number, lng: number, zoom: number) {
        console.log('Finished', uploaded, lat, lng);
        imageCreator.createImageMarkers(
            uploaded,
            lat,
            lng,
            // Prefer current zoom but fallback to previous zoom
            ctxEdit.mapRef?.current?.getMap().getZoom() ?? zoom,
            getOffsetForMultiUpload(lat, lng)
        );

        const newIconUrls = uploaded.map((e) => e.data.imageUrls[EUploadResolution.SMALL]);
        const newImageUrls = uploaded.map((e) => e.data.imageUrls[EUploadResolution.BIG]);
        ctxEdit.updateMap({
            imageGallery: [...newImageUrls, ...ctxEdit.map.imageGallery],
            iconGallery: [...newIconUrls, ...ctxEdit.map.iconGallery]
        });
        setBatchInProgress(false);
    }

    if (imageUploader.inProgress === 0) {
        return <></>;
    }

    return (
        <S_ImageUploader>
            <div className="message" ref={(el) => (props.controlsRef.current[10] = el)}>
                <LabelText fontSize={ELabelTextSize.MEDIUM}>
                    Please wait, files are currently being uploaded. Remaining:{' '}
                    <b>{imageUploader.inProgress}</b>
                </LabelText>
            </div>
        </S_ImageUploader>
    );
};

const S_ImageUploader = styled.div`
    position: absolute;
    top: 20px;
    display: flex;
    justify-content: center;
    width: 100%;
    pointer-events: none;
    > .message {
        pointer-events: all;
        padding: 20px;
        border-radius: 5px;
        white-space: nowrap;
        z-index: 100;
        background: #fffde7;
        border: 1px solid #bbb66b;
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
        border-radius: 5px;
    }
`;
